import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UUID_PATTERN } from 'src/app/shared/constants';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActionService } from 'src/app/core/services/action.service';
import { SyncData } from 'src/app/core/state/_actions/form.actions';
import { OpTypes, Select2Data } from 'src/app/core/state/_models/general.model';
import { getFormOperations, getSyncError } from 'src/app/core/state/_selectors/form.selector';
import { DatatableColumn } from 'src/app/shared/models/components.models';
import { ErrorComponent } from '../bulk-update/error/error.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SubSink } from 'subsink';
import * as moment from 'moment';

@Component({
  selector: 'app-inline-editor',
  templateUrl: './inline-editor.component.html',
  styleUrls: ['./inline-editor.component.scss']
})
export class InlineEditorComponent implements OnInit {
  @Input('col') col!: any;
  @Input('row') row: any;
  @Input('value') value: any;
  @Input('tableId') tableId: any;
  @Output() editSubmit = new EventEmitter<any>();
  @Output() editCancel = new EventEmitter<any>();
  @ViewChild('input') input!: TemplateRef<any>;
  @ViewChild('selectSearchScroll') selectSearchScroll!: TemplateRef<any>;
  @ViewChild('textarea') textarea!: TemplateRef<any>;
  @ViewChild('select') select!: TemplateRef<any>;
  @ViewChild('select2') select2!: TemplateRef<any>;
  @ViewChild('checkbox') checkbox!: TemplateRef<any>;
  
  form!:UntypedFormGroup;
  syncDataCalled:boolean = false;
  subsink = new SubSink();
  
  constructor(
    private fb:UntypedFormBuilder,
    private cd:ChangeDetectorRef,
    private action:ActionService,
    private store:Store,
    private modal:NgbModal
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      Value: ['']
    });
    this.form.controls.Value.setValue(this.getValue());
    if(!this.col.IsNullable && this.col.fieldType === 'PII2'){
      this.form.controls.Value.setValidators(Validators.required)
    }
    if (['CHAR(36)', 'UUID'].includes(this.col.fieldType)) {
      this.form.controls.Value.setValidators(Validators.pattern(UUID_PATTERN));
    }
    if(this.col.fieldType === 'PII2' || this.col.fieldType === 'PII'){
      this.col.Name = this.col.fieldName
    }
    this.cd.detectChanges();
  }

  getValue(){
    if(this.col.fieldType === 'ENUM2'){
      return this.getEnumFieldValue();
    } else if(this.col.fieldType === 'TINYINT(1)'){
      return this.value === 'True' ? true:false
    } else if(this.value.includes('$')){
      return this.value.replace(/[^0-9.]/g, '');
    } else if(this.col.fieldType === 'DATE'){
      return moment(this.value)?.format('YYYY-MM-DD')
    } else if(this.col.fieldType === 'DATETIME'){
      return moment(this.value,'MM-DD-YYYY hh:mm:ss A')?.format('YYYY-MM-DD HH:mm:ss')
    } else {
      return this.value;
    }
  }

  getEnumFieldValue(){
    return this.value ? this.value.split(';') : [];
  }

  inputTypeSelector = (col: DatatableColumn) => {
    if (col.isRelation) {
      return this.selectSearchScroll;
    }
    if (
      col.dropdownValues &&
      col.dropdownValues.length > 0 &&
      col.fieldType != 'USER' &&
      col.fieldType != 'ENUM2'
    )
      return this.select;
    let type = col.fieldType;

    switch (type) {
      case 'TEXT':
        return this.textarea;
      case 'TINYINT(1)':
        return this.checkbox;
      case 'ENUM':
        return this.select;
      case 'ENUM2':
        return this.select2;
      case 'USER':
        return this.selectSearchScroll;
      case 'INT':
      case 'VARCHAR(255)':
      case 'VARCHAR':
      default:
        return this.input;
    }
  };

  inputFieldType = (field: DatatableColumn) => {
    switch (field.fieldType) {
      case 'DATETIME':
        return 'datetime-local';
      case 'DATE':
        return 'date';
      case 'EMAIL':
        return 'email';
      case 'INT':
      case 'DOUBLE':
      case 'DECIMAL(20,2)':
        return 'number';
      case 'VARCHAR(255)':
      default:
        if (field.fieldType === 'number') return 'number';
        return 'text';
    }
  };

  inputFieldMask = (col: DatatableColumn) => {
    if (col.fieldType === 'VARCHAR(15)') return '(000) 000-0000';
    return '';
  };

  save(){
    this.editSubmit.emit(this.getFieldPayloadValue());
  }

  cancel(){
    this.editCancel.emit();
  }

  onActionSubmit = async () => {
    if(!this.form.value.Value) return;
    this.getSyncDataError();
    this.syncDataCalled = true;
    await this.action.dispatch(
      OpTypes.UPDATE,
      new SyncData(this.tableId,this.getEditTablePayload()),
      getFormOperations
    ).then(()=> {
      this.save()
    }).catch(err => {})
  }

  getEditTablePayload(){
    const fieldList = [{
      Name:this.col.prop.split('_').slice(1).join('_'),
      Type:this.col.fieldType,
      Info:null,
      Value:this.getFieldPayloadValue(),
      Base64EncodedFile:''
    }]
    return [{
      ApplicationTableKey: this.tableId,
      ActionType:'update',
      FieldsList:fieldList,
      Where: {
        Rid:this.row.id
      } 
    }]
  }

  getFieldPayloadValue(){
    if(this.col.fieldType === 'ENUM2'){
      return this.form.value.Value.join(";")
    } else if(this.col.fieldType === 'DECIMAL(20,2)'){
      return String(this.form.value.Value);
    } else if(this.col.fieldType === 'TINYINT(1)'){
      return this.form.value.Value ? 'True': 'False';
    } else if(this.col.fieldType === 'DATE' && this.form.value.Value) {
      return moment(this.form.value.Value)?.format('MM/DD/yyyy')
    } else if(this.col.fieldType === 'DATETIME' && this.form.value.Value) {
      return moment(this.form.value.Value)?.format('MM/DD/YYYY hh:mm A zz')
    } else {
      return this.form.value.Value;
    }
  }

  getPIIField = (field: any) => {
    return field.fieldType === 'PII' || field.fieldType === 'PII2'
      ? field
      : null;
  };

  piiValue(){
    return this.row[this.col.fieldName + '_decrypted'];
  }

  inputFieldPlaceholder = (field: any) => {
    switch (field.fieldType) {
      case 'PII':
      case 'PII2':
        if (field.IsNullable)
          return 'Type "delete" to remove value';
        return 'Value can be only replaced!';
      case 'VARCHAR(15)':
        return '(000) 000-0000';
      default:
        return 'Enter here';
    }
  };

  getSyncDataError(){
    let errorSubject = new Subject();
    this.subsink.add(
      this.store.pipe(
        select(getSyncError),
        takeUntil(errorSubject)
      ).subscribe((err)=> {
        if(this.syncDataCalled && err){
          this.syncDataCalled = false
          if ((err as any)?.error?.errors > 0) {
            const modal = this.modal.open(ErrorComponent, {
              windowClass: 'modal-custom',
              centered: true,
              backdrop: 'static',
              animation: true,
              keyboard: false,
              size: 'lg',
            });
            modal.componentInstance.response = err.error;
            modal.componentInstance.isBulkupdate = false;
            errorSubject.next();
            errorSubject.complete();
          }
        }
      }));
  }

  ngOnDestroy(){
    this.subsink.unsubscribe();
  }

}
